import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, CssBaseline, ThemeProvider } from '@mui/material'
import theme from './theme/theme'
import { loadKpiConfig }        from './reducers/kpiConfigReducer'
import { loadFilterOptions, loadActivityPhases, clearSelected } from './reducers/activityReducer'
import { setDatesFromActivity, resetFilters }    from './reducers/filtersReducer'
import { clearKpiData }          from './reducers/kpiDataReducer'
import { applyAndFetchKpiData } from './actions/kpiDataActions'

import ActivityKPIDashboardEmptyState     from './components/layout/ActivityKPIDashboardEmptyState'
import ActivityKPIDashboardActivityPicker from './components/filters/ActivityKPIDashboardActivityPicker'
import ActivityKPIDashboardFilterBar      from './components/filters/ActivityKPIDashboardFilterBar'
import ActivityKPIDashboardKpiGrid        from './components/charts/ActivityKPIDashboardKpiGrid'
import ActivityKPIDashboardLegend         from './components/charts/ActivityKPIDashboardLegend'
import { LegendResizeHandle, LEGEND_DEFAULT_W } from './components/charts/ActivityKPIDashboardLegendResize'

export default function App() {
  const dispatch = useDispatch()
  const selected = useSelector(s => s.activity.selected)
  const [pickerOpen,   setPickerOpen]   = useState(false)
  const [showMarks,    setShowMarks]    = useState(true)
  const [showLegend,   setShowLegend]   = useState(false)
  const [legendW,      setLegendW]      = useState(LEGEND_DEFAULT_W)
  const gridRef      = useRef(null)

  // Load config on mount
  useEffect(() => {
    dispatch(loadKpiConfig())
  }, []) // eslint-disable-line


  const handleActivityConfirmed = useCallback((detail) => {
    dispatch(setDatesFromActivity({
      startDate:  detail.startDate,
      endDate:    detail.endDate,
      technology: detail.technology,
    }))
    dispatch(clearKpiData())
    dispatch(loadFilterOptions({ id: detail.activityId, filters: {} }))
    dispatch(loadActivityPhases(detail.activityId))
    dispatch(applyAndFetchKpiData())
  }, [dispatch])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <ActivityKPIDashboardActivityPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={handleActivityConfirmed}
        />

        {!selected ? (
          <ActivityKPIDashboardEmptyState onBrowse={() => setPickerOpen(true)} />
        ) : (
          <>
            {/* Filter bar + agg banner — full width */}
            <Box sx={{ flexShrink: 0 }}>
              <ActivityKPIDashboardFilterBar onOpenPicker={() => setPickerOpen(true)} showMarks={showMarks} onToggleMarks={() => setShowMarks(v => !v)} showLegend={showLegend} onToggleLegend={() => setShowLegend(v => !v)} gridRef={gridRef} activityTitle={selected?.title} />
            </Box>

            {/* Main area: charts + right legend panel */}
            <Box sx={{ display: 'flex', overflow: 'hidden', height: 'calc(100vh - 130px)' }}>
              {/* Charts — takes all available horizontal space */}
              <Box sx={{ flex: 1, minWidth: 0, p: '12px 16px', overflowY: 'auto' }}>
                <ActivityKPIDashboardKpiGrid showMarks={showMarks} gridRef={gridRef} />
              </Box>

              {/* Resize handle — only while the legend is expanded */}
              {showLegend && (
                <LegendResizeHandle onResize={setLegendW} onReset={() => setLegendW(LEGEND_DEFAULT_W)} />
              )}

              {/* Legend panel — resizable right side, same height as charts area */}
              <ActivityKPIDashboardLegend open={showLegend} onToggle={() => setShowLegend(v => !v)} width={legendW} />
            </Box>
          </>
        )}
      </Box>
    </ThemeProvider>
  )
}